import {
  ArrowRight,
  Bolt,
  CheckCircle2,
  Globe,
  Instagram,
  Server,
  Shield,
  ShoppingCart,
  Smartphone,
  Store,
  TrendingUp,
  Zap
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Refs for sections
  const featuresRef = useRef<HTMLElement>(null);
  const howItWorksRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);

  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    if (ref.current) {
      const navbarHeight = 80; // Approximate navbar height
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openWhatsApp = () => {
    window.open(`https://wa.me/917518388162`, '_blank');
  };

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleStartFree = () => {
    navigate('/login');
  };

  const handleStartSuccess = () => {
    navigate('/login');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* WhatsApp Support Button - Enhanced */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-3 shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse hover:animate-none group flex items-center gap-2"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp className="w-5 h-5" />
        <span className="font-medium">Chat with us</span>
        <div className="absolute -top-14 -left-20 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm whitespace-nowrap">
          Need help? Chat now! 💬
        </div>
      </button>

      {/* Navbar - Enhanced with Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-gray-100' 
        : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center space-x-3">
              {/* <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                <Store className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                <img src="/logo.png" alt="Mart-24" className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div> */}
              <img src="/logo_trans.png" alt="Mart-24" className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Mart-24
              </span>
            </div>

            {/* Navigation Links - Hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-8">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-gray-600 hover:text-purple-600 transition-colors font-medium"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection(featuresRef)}
                className="text-gray-600 hover:text-purple-600 transition-colors font-medium"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection(howItWorksRef)}
                className="text-gray-600 hover:text-purple-600 transition-colors font-medium"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection(pricingRef)}
                className="text-gray-600 hover:text-purple-600 transition-colors font-medium"
              >
                Pricing
              </button>
            </div>

            <button
              onClick={handleLogin}
              className="px-4 py-2 sm:px-6 sm:py-3 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all duration-300 text-white font-medium text-sm sm:text-base shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Completely Redesigned */}
      <section className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-1000"></div>
          <div className="absolute bottom-20 left-20 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 pt-28 sm:pt-36 lg:pt-44 pb-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-3 py-1.5 mb-4 bg-white/80 backdrop-blur-sm rounded-full border border-purple-200 shadow-sm">
              <Zap className="w-3.5 h-3.5 text-purple-600 mr-1.5" />
              <span className="text-xs font-medium text-gray-700">Launch Your Store in Under 2 Minutes</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent animate-gradient">
                Create Your Free Dream
              </span>
              <br />
              <span className="text-gray-900">E-commerce Store</span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 max-w-3xl mx-auto leading-relaxed">
              No coding required. Get started for free today! 🚀
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8">
              <button
                onClick={handleStartFree}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center justify-center gap-2 font-semibold text-base group"
              >
                Start Free Store
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              {/* <div className="flex items-center text-gray-600">
                <div className="flex -space-x-2 mr-3">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 border-2 border-white flex items-center justify-center">
                      <Heart className="w-2.5 h-2.5 text-white" />
                    </div>
                  ))}
                </div>
                <span className="text-xs font-medium">Loved by 2k+ entrepreneurs</span>
              </div> */}
            </div>

            {/* Social Proof */}
            <div className="grid grid-cols-3 gap-3 sm:gap-6 text-center">
              <div>
                <div className="text-xl font-bold text-gray-900">99.9%</div>
                <div className="text-xs text-gray-600">Uptime</div>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">24/7</div>
                <div className="text-xs text-gray-600">Support</div>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">₹0</div>
                <div className="text-xs text-gray-600">Setup Cost</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced */}
      <section ref={featuresRef} className="py-12 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 text-gray-900">
              Everything You Need to Succeed
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to help your business grow
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8">
            {[
              { icon: ShoppingCart, title: "Easy Product Management", desc: "Upload and manage products with drag & drop simplicity", color: "purple" },
              { icon: TrendingUp, title: "Real-time Analytics", desc: "Track sales, customers, and performance in real-time", color: "blue" },
              { icon: Server, title: "All-in-One Platform", desc: "Hosting, security, and maintenance included", color: "green" },
              { icon: Smartphone, title: "Mobile Optimized", desc: "Your store looks perfect on all devices", color: "pink" },
              { icon: Shield, title: "Secure & Reliable", desc: "Bank-level security with 99.9% uptime", color: "indigo" },
              { icon: Bolt, title: "Lightning Fast", desc: "Pages load in under 2 seconds worldwide", color: "yellow" }
            ].map((feature, index) => (
              <div key={index} className="group p-3 sm:p-8 bg-white rounded-xl sm:rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className={`w-10 h-10 sm:w-14 sm:h-14 bg-${feature.color}-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className={`w-5 h-5 sm:w-7 sm:h-7 text-${feature.color}-600`} />
                </div>
                <h3 className="text-sm sm:text-xl font-bold mb-1.5 sm:mb-3 text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-2">
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-base text-gray-600 leading-relaxed line-clamp-3 sm:line-clamp-none">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Enhanced */}
      <section ref={howItWorksRef} className="py-12 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 text-gray-900">
              Get Started in 3 Simple Steps
            </h2>
            <p className="text-base sm:text-xl text-gray-600">No technical skills required</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 relative">
              {/* Connection Lines - Hidden on mobile, visible on desktop */}
              <div className="hidden md:block absolute top-24 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-purple-300 to-blue-300"></div>
              
              {[
                { step: 1, title: "Sign Up Free", desc: "Create your account in seconds", icon: "🚀" },
                { step: 2, title: "Customize Store", desc: "Add products and personalize design", icon: "🎨" },
                { step: 3, title: "Start Selling", desc: "Go live and accept orders instantly", icon: "💰" }
              ].map((item, index) => (
                <div key={index} className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
                  {/* Step Number - Centered on all screens */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg z-10">
                    <span className="text-sm font-bold text-white">{item.step}</span>
                  </div>

                  {/* Icon Container */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mt-4 sm:mt-0 mb-4 sm:mb-6 shadow-lg transform hover:scale-105 transition-transform duration-300">
                    <span className="text-2xl sm:text-3xl">{item.icon}</span>
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-900">{item.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>

                  {/* Mobile Connection Line */}
                  {index < 2 && (
                    <div className="md:hidden absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b from-purple-300 to-blue-300"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing - Enhanced */}
      <section ref={pricingRef} className="py-12 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 text-gray-900">
              Choose Your Perfect Plan
            </h2>
            <p className="text-base sm:text-xl text-gray-600">Start free, upgrade as you grow</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Free Plan */}
              <div className="bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-purple-300 transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-2">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full mb-4">
                    <span className="text-sm font-medium text-gray-700">Perfect to Start</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">Free Forever</h3>
                  <div className="text-5xl font-bold text-purple-600 mb-2">₹0</div>
                  <p className="text-gray-600">No hidden charges</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {[
                    "Professional subdomain",
                    "Up to 20 products",
                    "Secure payment gateway", 
                    "Mobile-responsive design",
                    "Basic analytics",
                    "Whatsapp support"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleStartFree}
                  className="w-full py-4 bg-gray-100 text-gray-900 rounded-2xl hover:bg-gray-200 transition-all duration-300 font-semibold"
                >
                  Start Free
                </button>
              </div>

              {/* Premium Plan */}
              <div className="bg-gradient-to-b from-purple-50 to-blue-50 rounded-3xl p-8 border-2 border-purple-200 relative shadow-xl transform scale-105">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-2 rounded-full font-medium">
                    🔥 Most Popular
                  </div>
                </div>

                <div className="text-center mb-8 mt-4">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">Premium Plan</h3>
                  <div className="text-5xl font-bold text-blue-600 mb-2">2%</div>
                  <p className="text-gray-600">of monthly sales</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {[
                    "Custom domain (yourbrand.com)",
                    "Unlimited products",
                    "Advanced customization",
                    "Multiple payment options",
                    "Discount codes & variants",
                    "Google Ads integration",
                    "Priority support",
                    "Advanced analytics"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleGetStarted}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 font-semibold shadow-lg"
                >
                  Upgrade to Premium
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Enhanced */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your Business? 🚀
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of successful entrepreneurs who've already launched their dream stores with Mart-24
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <button
                onClick={handleStartSuccess}
                className="w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-xl font-semibold text-lg"
              >
                Start Your Success Story
              </button>
              <span className="text-gray-400">or</span>
              <button
                onClick={openWhatsApp}
                className="w-full sm:w-auto px-8 py-4 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all duration-300 font-semibold flex items-center justify-center gap-2"
              >
                <FaWhatsapp className="w-5 h-5" />
                Chat with Expert
              </button>
            </div>

            <div className="text-sm text-gray-400">
              ✅ No setup fees  ✅ No monthly charges  ✅ Cancel anytime
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Enhanced */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Store className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Mart-24
                </span>
              </div>
              <p className="text-gray-600 mt-2 max-w-md">
                Empowering entrepreneurs to build successful online stores
              </p>
            </div>
            
            <div className="flex space-x-6">
              <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors transform hover:scale-110">
                <Instagram className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors transform hover:scale-110">
                <Globe className="w-6 h-6" />
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600">
              © 2024 Mart-24. Made with ❤️ for entrepreneurs worldwide.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};